/**
 * FiCockpit — top-level owner cockpit for the FI (Finanzas) module.
 * Fetches /api/admin/fi/overview (+ optionally saas-metrics for NRR) on
 * mount and composes the full set of FI cockpit components.
 */
import { useEffect, useState } from 'react'
import { api } from '../../../services/api'
import type { FiOverviewResponse } from './fiTypes'
import FiKpiRow from './FiKpiRow'
import FiIncomeCostChart from './FiIncomeCostChart'
import FiMarginTrendChart from './FiMarginTrendChart'
import FiCostBreakdownChart from './FiCostBreakdownChart'
import FiGroupSplitChart from './FiGroupSplitChart'
import FiServiceTypeSplitChart from './FiServiceTypeSplitChart'
import FiTopClientsMarginTable from './FiTopClientsMarginTable'
import FiPipelinePanel from './FiPipelinePanel'

interface FiCockpitProps {
  months?: number
}

export default function FiCockpit({ months = 12 }: FiCockpitProps) {
  const [overview, setOverview] = useState<FiOverviewResponse | null>(null)
  const [nrr, setNrr] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [months])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      // saas-metrics (NRR) is additive — the cockpit still renders without it.
      const [overviewResult, saasResult] = await Promise.allSettled([
        api.getFiOverview(months),
        api.getSaasMetrics(),
      ])

      if (overviewResult.status !== 'fulfilled' || !overviewResult.value.success || !overviewResult.value.data) {
        throw new Error(overviewResult.status === 'rejected'
          ? String(overviewResult.reason)
          : overviewResult.value.success === false ? (overviewResult.value.error || 'Error al cargar FI overview') : 'Error al cargar FI overview')
      }
      // apiRequest double-wraps: response body is { success, data: FiOverviewResponse },
      // apiRequest wraps that again as { success, data: <body> }. Unwrap the inner `.data`.
      const rawOverview = overviewResult.value.data as unknown as { data?: FiOverviewResponse } | FiOverviewResponse
      const payload = (rawOverview as { data?: FiOverviewResponse }).data ?? (rawOverview as FiOverviewResponse)
      setOverview(payload)

      if (saasResult.status === 'fulfilled' && saasResult.value.success && saasResult.value.data) {
        const rawSaas = saasResult.value.data as unknown as { data?: { growth?: { nrr?: number | null } } }
        setNrr(rawSaas.data?.growth?.nrr ?? null)
      }
    } catch (err) {
      console.error('Error fetching FI overview:', err)
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-24 bg-gray-800 rounded-xl" />
          <div className="h-80 bg-gray-800 rounded-xl" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="h-64 bg-gray-800 rounded-xl" />
            <div className="h-64 bg-gray-800 rounded-xl" />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg">
        {error}
        <button onClick={fetchData} className="ml-4 underline hover:no-underline">Reintentar</button>
      </div>
    )
  }

  if (!overview) return null

  const periodLabels = overview.trend.map(p => p.label)

  return (
    <div className="space-y-6">
      {overview.costCoverage.incomplete && (
        <div className="bg-amber-500/10 border border-amber-500/40 text-amber-300 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <div>
            <p className="font-medium">Cobertura de costos incompleta</p>
            <p className="text-amber-300/80 mt-0.5">
              {overview.costCoverage.servicesWithConfiguredCost} de {overview.costCoverage.servicesTotal} servicios
              tienen costo configurado. El margen puede estar sobreestimado para:{' '}
              <span className="font-medium">{overview.costCoverage.missingCostVendors.join(', ') || '—'}</span>.
            </p>
          </div>
        </div>
      )}

      <FiKpiRow current={overview.current} pipeline={overview.pipeline} nrr={nrr} />

      <FiIncomeCostChart trend={overview.trend} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FiMarginTrendChart trend={overview.trend} />
        <FiCostBreakdownChart costBreakdown={overview.costBreakdown} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FiGroupSplitChart groupSplit={overview.groupSplit} />
        <FiServiceTypeSplitChart serviceTypeSplit={overview.serviceTypeSplit} />
      </div>

      <FiTopClientsMarginTable rows={overview.topClientsByMargin} periodLabels={periodLabels} />

      <FiPipelinePanel pipeline={overview.pipeline} />
    </div>
  )
}
